import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { BadgeCheck, ShieldCheck, Star } from "lucide-react";
import { MEDIA } from "@/config/media";
import { WordReveal } from "./Reveal";
import { LeadForm } from "./LeadForm";

const PROOF = ["RERA-verified projects only", "Free advisory for buyers", "Zero builder bias"];

export const Hero = ({ stats }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "16%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1.04, 1.14]);

  return (
    <section
      ref={ref}
      id="top"
      data-testid="hero-section"
      className="relative overflow-hidden bg-ink pb-20 pt-24 lg:pb-24 lg:pt-36"
    >
      <motion.div style={{ y: imgY, scale: imgScale }} className="absolute inset-0">
        <img
          src={MEDIA.hero}
          alt="Premium residential towers along the NH-24 corridor at dusk"
          className="h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-ink via-ink/88 to-ink/45" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-transparent to-ink/45" />
      </motion.div>

      <div className="relative mx-auto grid max-w-[1280px] grid-cols-1 gap-10 px-5 lg:grid-cols-12 lg:gap-14 lg:px-8">
        <div className="order-2 lg:order-none lg:col-span-6 lg:pt-6">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15, duration: 0.6 }}
            className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3.5 py-1.5 ring-1 ring-white/15"
          >
            <ShieldCheck className="h-3.5 w-3.5 text-brand" />
            <span className="text-xs font-semibold text-white/85">
              Consultancy &amp; advisory firm — not a builder
            </span>
          </motion.div>

          <h1 className="mt-6 hidden max-w-xl font-display text-4xl font-extrabold leading-[1.05] text-white sm:text-5xl lg:block lg:text-[3.4rem]">
            <WordReveal text="Find the right home" delay={0.1} />
            <span className="mt-1 block text-brand">
              <WordReveal text="on the NH-24 corridor" delay={0.4} />
            </span>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.75, duration: 0.6 }}
            className="mt-6 max-w-xl text-base leading-relaxed text-white/70 md:text-lg"
          >
            Independent advice on Wave City, Aditya World City and Siddharth Vihar. We compare every
            builder, verify every paper and negotiate on your side of the table.
          </motion.p>

          <motion.ul
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.9, duration: 0.6 }}
            className="mt-7 flex flex-wrap gap-x-6 gap-y-3"
          >
            {PROOF.map((p) => (
              <li key={p} className="flex items-center gap-2 text-sm text-white/75">
                <BadgeCheck className="h-4 w-4 shrink-0 text-brand" />
                {p}
              </li>
            ))}
          </motion.ul>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.05, duration: 0.6 }}
            className="mt-10 flex flex-wrap items-center gap-8 border-t border-white/10 pt-7"
          >
            {[
              { v: stats ? `${stats.families_advised}+` : "480+", k: "Families advised" },
              { v: stats ? `${stats.site_visits}+` : "1,200+", k: "Site visits hosted" },
              { v: "48 hrs", k: "Shortlist turnaround" },
            ].map((m) => (
              <div key={m.k}>
                <div className="font-display text-2xl font-extrabold text-white">{m.v}</div>
                <div className="mt-0.5 text-xs text-white/50">{m.k}</div>
              </div>
            ))}
            <div className="flex items-center gap-1.5">
              {[0, 1, 2, 3, 4].map((i) => (
                <Star key={i} className="h-3.5 w-3.5 fill-brand text-brand" />
              ))}
              <span className="ml-1 text-xs text-white/60">4.9 average client rating</span>
            </div>
          </motion.div>
        </div>

        <div className="order-1 lg:order-none lg:col-span-5 lg:col-start-8">
          <div className="mb-4 lg:hidden">
            <h2 className="font-display text-2xl font-extrabold leading-tight text-white">
              Find the right home on <span className="text-brand">NH-24</span>
            </h2>
          </div>
          <LeadForm />
        </div>
      </div>
    </section>
  );
};
